import { useAppSelector } from '@brands/hooks/useReduxHook';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { useEffect, useState } from 'react';

const toPatientId = (value: unknown): number | undefined => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

/**
 * Resolves the patient the availability lists should be filtered for.
 *
 * The intake flow fills `patientForm.selectedPatient` on the SelectPatient
 * step, so the scheduling pages could read it directly. A reschedule started
 * from an email link never passes through that step — the patient lands on
 * `/select-provider?reschedule=...&caseId=...` straight after logging in, with
 * an empty patientForm — and the pages were then sending `patient_id=` (empty
 * string), which the backend rejects with a 500 because it expects an int.
 *
 * The linked case is the only reliable source in that situation: the signed-in
 * user is not necessarily the case's patient (a guardian rescheduling a
 * dependent's appointment), so it is consulted before falling back to self.
 * Returns undefined when nothing resolves, so callers can omit the param
 * entirely rather than send a blank one.
 */
export const useAvailabilityPatientId = (rescheduleCaseId?: number | null): number | undefined => {
  const { userInfo } = useAppSelector(selectAuth);
  const { selectedPatient } = useAppSelector(selectPatientForm);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const [casePatientId, setCasePatientId] = useState<number | undefined>();

  const formPatientId = toPatientId(selectedPatient?.value);
  const staffSelectedPatientId = toPatientId(currentPatient?.id);
  const isSelfSchedulingRole =
    userInfo?.role?.name === UserRoleName.Patient || userInfo?.role?.name === UserRoleName.Dependent;
  const selfPatientId = isSelfSchedulingRole ? toPatientId(userInfo?.id) : undefined;

  const needsCaseLookup = !formPatientId && !staffSelectedPatientId && !!rescheduleCaseId;

  useEffect(() => {
    if (!needsCaseLookup || !rescheduleCaseId) return undefined;

    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const thisCase = await getOneCase(rescheduleCaseId);
        if (!cancelled) {
          setCasePatientId(toPatientId(thisCase?.patient_id ?? thisCase?.patient?.id));
        }
      } catch (error: unknown) {
        // Non-fatal: the availability request just goes out without a
        // patient_id filter, which is still better than a blank one. Swallow
        // silently — surfacing this to the patient would be more alarming
        // than the degraded (but working) fallback it's masking.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [needsCaseLookup, rescheduleCaseId]);

  return formPatientId ?? staffSelectedPatientId ?? casePatientId ?? selfPatientId;
};

export default useAvailabilityPatientId;
